import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, Shield, Stethoscope, TrendingUp, Pill, Calendar, MessageSquare, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import MobileNavDrawer from "@/components/MobileNavDrawer";
import NotificationBell from "@/components/NotificationBell";
import BrandLogo from "@/components/BrandLogo";

const ROLE_VIEWS = [
  { label: "Admin", path: "/admin", icon: Shield },
  { label: "Provider", path: "/provider", icon: Stethoscope },
  { label: "Sales", path: "/sales", icon: TrendingUp },
  { label: "Pharmacy", path: "/pharmacy", icon: Pill },
] as const;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isProvider, isSalesperson, isPharmacy, loading: roleLoading } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = useCallback((hash: string) => {
    const scrollTo = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    };
    if (location.pathname !== "/") {
      navigate("/#" + hash);
      setTimeout(scrollTo, 150);
    } else {
      scrollTo();
    }
  }, [location.pathname, navigate]);

  const rolesReady = !authLoading && !roleLoading;

  // Determine current active view label for the dropdown trigger
  const activeView = ROLE_VIEWS.find((v) => location.pathname.startsWith(v.path));

  // Build the list of views this user can access
  const availableViews = rolesReady
    ? ROLE_VIEWS.filter((v) => {
        if (isAdmin) return true; // admins see all
        if (v.path === "/provider" && isProvider) return true;
        if (v.path === "/sales" && isSalesperson) return true;
        if (v.path === "/pharmacy" && isPharmacy) return true;
        return false;
      })
    : [];

  const showSwitcher = availableViews.length > 0;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto flex items-center justify-between h-16 md:h-20 px-4">
        <BrandLogo />

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/peptides" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Therapies</Link>
          {!user && (
            <>
              <button onClick={() => scrollToSection("how-it-works")} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">How It Works</button>
              <button onClick={() => scrollToSection("pricing")} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Pricing</button>
            </>
          )}
          {user && (
            <>
              <Link to="/book" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
                <Calendar size={14} />
                Book Appointment
              </Link>
              <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
                <MessageSquare size={14} />
                Messages
              </Link>
              <Link to="/settings" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5">
                <Settings size={14} />
                Settings
              </Link>
            </>
          )}
          {!user && (
            <DropdownMenu>
              <DropdownMenuTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                For Professionals
                <ChevronDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/join/provider" className="flex items-center gap-2">
                    <Stethoscope size={14} />
                    For Providers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/join/sales" className="flex items-center gap-2">
                    <TrendingUp size={14} />
                    For Sales Partners
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/join/pharmacy" className="flex items-center gap-2">
                    <Pill size={14} />
                    For Pharmacies
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              {showSwitcher && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5">
                      {activeView ? (
                        <>
                          <activeView.icon size={14} />
                          {activeView.label}
                        </>
                      ) : (
                        "Views"
                      )}
                      <ChevronDown size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {availableViews.map((v) => (
                      <DropdownMenuItem key={v.path} asChild>
                        <Link
                          to={v.path}
                          className={`flex items-center gap-2 ${location.pathname.startsWith(v.path) ? "font-semibold text-primary" : ""}`}
                        >
                          <v.icon size={14} />
                          {v.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                    {isProvider && (
                      <DropdownMenuItem asChild>
                        <Link to="/provider/profile" className="flex items-center gap-2">
                          <Stethoscope size={14} />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <NotificationBell />
              <Link to="/dashboard">
                <Button size="sm">Dashboard</Button>
              </Link>
            </div>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm">Log In</Button>
              </Link>
              <Link to="/get-started">
                <Button size="sm">Get Started</Button>
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden text-foreground" onClick={() => setIsOpen(true)}>
          <Menu size={24} />
        </button>
      </div>

      <MobileNavDrawer open={isOpen} onOpenChange={setIsOpen} />
    </nav>
  );
};

export default Navbar;